// Maintenance reminders ("watches") on a vehicle — the ONE rule for how close one is.
//
// WHY: a workshop says "FNQ68H will need brakes in about 5,000–10,000 km". That isn't a
// service record (nothing has happened yet) and it isn't a van-check fault (nothing is
// wrong yet), so before this it lived in someone's head. A watch holds it against the
// odometer until it's done, and escalates — office AND the van's driver — as it closes in.
//
// CommonJS on purpose (same reason as odoCheck.js): the Service & Repairs page, the
// driver's Van Check, the API and the daily alert job all import it, so the page can
// never show "on track" while the job texts the driver "overdue".
//
// A watch is stored with ABSOLUTE odometer figures (fromKm/toKm), never "in 5,000 km":
// a relative number means nothing a week later. The page converts at entry time and keeps
// the basis it used (baseKm) so the reminder can say where the numbers came from.

const DEFAULT_LEAD_KM = 1000;   // start warning this far before the window opens
const DATE_LEAD_DAYS = 14;      // …or this many days before a due date

// Stages in escalation order. The alert job texts once per stage it climbs INTO, so a
// watch sends at most three texts in its life however many days it sits at a stage.
const STAGES = ['watch', 'soon', 'due', 'over'];
const stageRank = (s) => Math.max(0, STAGES.indexOf(s));

const num = (v) => { const n = Number(String(v == null ? '' : v).replace(/[^0-9.]/g, '')); return Number.isFinite(n) ? n : 0; };
const fmtN = (n) => Math.round(Number(n) || 0).toLocaleString('en-AU');
const dayDiff = (fromISO, toISO) => {
  if (!fromISO || !toISO) return null;
  const a = Date.parse(fromISO + 'T00:00:00Z'), b = Date.parse(toISO + 'T00:00:00Z');
  return Number.isFinite(a) && Number.isFinite(b) ? Math.round((b - a) / 86400000) : null;
};

// odo: the van's current odometer (0/null = unknown). today: 'YYYY-MM-DD' Sydney.
// → { stage, kmToFrom, kmToTo, daysLeft, label }
//   watch — nothing to do yet
//   soon  — within leadKm of the window (or DATE_LEAD_DAYS of the date): book it in
//   due   — the odometer is inside the window (or the date is today)
//   over  — past the far end of the window, or past the date
function watchStanding(w, odo, today) {
  const from = num(w && w.fromKm);
  const to = Math.max(num(w && w.toKm), from);
  const lead = num(w && w.leadKm) || DEFAULT_LEAD_KM;
  const cur = num(odo);
  const hasKm = from > 0 && cur > 0;
  const kmToFrom = hasKm ? from - cur : null;
  const kmToTo = hasKm ? to - cur : null;
  const daysLeft = (w && w.dueDate) ? dayDiff(today, w.dueDate) : null;

  let kmStage = 'watch';
  if (hasKm) {
    if (cur > to) kmStage = 'over';
    else if (cur >= from) kmStage = 'due';
    else if (kmToFrom <= lead) kmStage = 'soon';
  }
  let dateStage = 'watch';
  if (daysLeft != null) {
    if (daysLeft < 0) dateStage = 'over';
    else if (daysLeft === 0) dateStage = 'due';
    else if (daysLeft <= DATE_LEAD_DAYS) dateStage = 'soon';
  }
  // Whichever measure is more urgent wins — same as the service Due pill.
  const stage = stageRank(dateStage) > stageRank(kmStage) ? dateStage : kmStage;

  const parts = [];
  if (hasKm && kmStage === 'over') parts.push(`${fmtN(cur - to)} km past ${fmtN(to)} km`);
  else if (hasKm && kmStage === 'due') parts.push(to > from ? `in the window — ${fmtN(kmToTo)} km left` : 'due now');
  else if (hasKm) parts.push(`${fmtN(kmToFrom)} km to go`);
  else if (from > 0) parts.push(`due from ${fmtN(from)} km`);
  if (daysLeft != null) parts.push(daysLeft < 0 ? `${-daysLeft} days overdue` : daysLeft === 0 ? 'due today' : `${daysLeft} days to go`);
  return { stage, kmToFrom, kmToTo, daysLeft, label: parts.join(' · ') };
}

// "24,983–29,983 km" / "from 24,983 km" / "by 1 Dec 2026" — the target, in words.
function watchTarget(w) {
  const from = num(w && w.fromKm), to = num(w && w.toKm);
  const parts = [];
  if (from > 0) parts.push(to > from ? `${fmtN(from)}–${fmtN(to)} km` : `${fmtN(from)} km`);
  if (w && w.dueDate) {
    let d = w.dueDate;
    try { d = new Date(w.dueDate + 'T00:00:00').toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }); } catch (_) { /* keep ISO */ }
    parts.push(`by ${d}`);
  }
  return parts.join(' or ');
}

// The driver's text for a stage. Kept short (one SMS segment where possible) and plain —
// the driver isn't being asked to arrange anything, only to know and to speak up.
// Dashes are swapped for plain hyphens: one en/em dash switches a text to UCS-2, where a
// segment is 70 characters instead of 160 and this message would bill as three.
function driverMessage(w, standing) {
  return smsText(w, standing).replace(/[\u2013\u2014]/g, '-');
}
function smsText(w, standing) {
  const what = (w.item || 'Maintenance').trim();
  const rego = w.rego;
  if (standing.stage === 'over') {
    return `Hollypaul: ${rego} is past due for ${what.toLowerCase()} (${watchTarget(w)}). If it hasn't been booked in, tell the office today.`;
  }
  if (standing.stage === 'due') {
    return `Hollypaul: ${rego} has reached the km for ${what.toLowerCase()} (${watchTarget(w)}). The office is booking it in — tell us straight away if it feels or sounds wrong.`;
  }
  if (standing.stage === 'soon') {
    return `Hollypaul: ${rego} is due for ${what.toLowerCase()} soon (${standing.label}). The office will book it in — if it feels or sounds wrong, mark it Action on your Van Check.`;
  }
  return `Hollypaul: heads-up — ${rego} will need ${what.toLowerCase()} at ${watchTarget(w)}. It's on your Van Check. If it feels or sounds wrong before then, mark it Action.`;
}

module.exports = { DEFAULT_LEAD_KM, DATE_LEAD_DAYS, STAGES, stageRank, watchStanding, watchTarget, driverMessage };
